import { useRef, type FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { useAccountGateBypass } from '@/hooks/useAccountGateBypass'
import { platformNoticeApi, type PlatformNoticeState } from '@/services/api/platform-notice.api'
import { PLATFORM_NOTICE_QUERY_KEY, usePlatformNotice, useNoticeText } from './usePlatformNotice'
import { SupportEmailText } from './SupportEmailText'

// Escape and outside clicks call onClose; ignoring them means the only way out
// is the confirmation button (#2072: "do not allow the modal to be dismissed
// without acknowledgment").
const ignoreDismiss = () => {}

/**
 * #2072 — entry modal for the active platform notice. Shown until the user
 * confirms; the server stores the confirmation per noticeId, so a refresh or a
 * new login does not bring it back. Never shown to an impersonating admin: the
 * confirmation belongs to the account owner (#1207 / #1344).
 */
export const PlatformNoticeModal: FC = () => {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()
  const { shouldBypassAccountGates } = useAccountGateBypass()
  const { data } = usePlatformNotice()
  const text = useNoticeText(data?.notice)
  const confirmRef = useRef<HTMLButtonElement>(null)

  const confirm = useMutation({
    mutationFn: (noticeId: string) => platformNoticeApi.acknowledge(noticeId),
    onSuccess: () => {
      queryClient.setQueryData<PlatformNoticeState>(PLATFORM_NOTICE_QUERY_KEY, (prev) =>
        prev ? { ...prev, acknowledged: true, requiresAcknowledgement: false } : prev,
      )
    },
    onError: (err: { status?: number }) => {
      // The notice was switched off or replaced meanwhile: show what is live now.
      if (err?.status === 409) {
        void queryClient.invalidateQueries({ queryKey: PLATFORM_NOTICE_QUERY_KEY })
      }
    },
  })

  const notice = data?.notice
  const open = !!notice && !!data?.requiresAcknowledgement && !shouldBypassAccountGates
  if (!open) return null

  const failed = confirm.isError && (confirm.error as { status?: number })?.status !== 409

  return (
    <Dialog open onClose={ignoreDismiss} initialFocus={confirmRef} className="relative z-[60]">
      <div className="fixed inset-0 bg-neutral-900/60" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <div className="flex items-center gap-3">
            <InformationCircleIcon className="h-6 w-6 flex-shrink-0 text-primary-600" aria-hidden="true" />
            <Dialog.Title as="h2" className="heading-5">
              {text('title')}
            </Dialog.Title>
          </div>
          <Dialog.Description as="p" className="mt-4 whitespace-pre-line text-sm leading-6 text-neutral-700">
            <SupportEmailText text={text('message')} linkClassName="font-medium text-primary-600 underline hover:text-primary-700" />
          </Dialog.Description>
          {failed && (
            <p role="alert" className="mt-4 text-sm text-red-600">
              {t('platformNotice.acknowledgeFailed')}
            </p>
          )}
          <div className="mt-6 flex justify-end">
            <Button ref={confirmRef} onClick={() => confirm.mutate(notice.noticeId)} disabled={confirm.isPending}>
              {t('platformNotice.confirm')}
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
